import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { register } from '../../utils/queries';
import AuthCard, {
	FormError,
	FormSuccess,
	TextField,
	PasswordField,
	PASSWORD_HINT,
	newPasswordSchema,
} from './AuthCard';

const RegisterSchema = Yup.object().shape({
	firstname: Yup.string().trim().max(50, 'First name is too long.').required('First name is required.'),
	lastname: Yup.string().trim().max(255, 'Last name is too long.').required('Last name is required.'),
	email: Yup.string().trim().email('Enter a valid email address.').required('Email is required.'),
	...newPasswordSchema,
});

// Unlike print_fulfillment_ui, registering doesn't sign you in: the account
// stays inactive until the emailed verification link is opened (see
// pi_ecommerce_api adminAccount.service.ts for why), and it has no store
// access until an admin grants it.
export default function Register() {
	const [created, setCreated] = useState('');

	const handleSubmit = async (values, { setSubmitting, setStatus }) => {
		setStatus(undefined);

		const response = await register({
			firstname: values.firstname.trim(),
			lastname: values.lastname.trim(),
			email: values.email.trim(),
			password: values.password,
		});
		setSubmitting(false);

		if (response?.error) {
			setStatus(response.error);
			return;
		}

		setCreated(response?.data || 'Account created. Check your email for a link to activate your account.');
	};

	return (
		<AuthCard
			title={created ? 'Check your email' : 'Create your account'}
			subtitle={created ? undefined : 'Get started with PI eCommerce Admin'}
		>
			<FormSuccess message={created} />

			{created ? (
				<p className='text-sm text-gray-600'>
					Once your email is verified you can sign in. An administrator will need to give you access to a
					store before you can manage it.
				</p>
			) : (
				<Formik
					initialValues={{ firstname: '', lastname: '', email: '', password: '', confirmPassword: '' }}
					validationSchema={RegisterSchema}
					onSubmit={handleSubmit}
				>
					{({ isSubmitting, status }) => (
						<Form className='space-y-4'>
							<FormError message={status} />

							<div className='grid grid-cols-2 gap-3'>
								<TextField name='firstname' label='First name' autoComplete='given-name' />
								<TextField name='lastname' label='Last name' autoComplete='family-name' />
							</div>
							<TextField
								name='email'
								label='Email address'
								type='email'
								placeholder='Enter your email'
								autoComplete='email'
							/>
							<PasswordField name='password' label='Password' hint={PASSWORD_HINT} />
							<PasswordField name='confirmPassword' label='Confirm password' />

							<button
								type='submit'
								disabled={isSubmitting}
								className='btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
							>
								{isSubmitting ? 'Creating account...' : 'Create account'}
							</button>
						</Form>
					)}
				</Formik>
			)}

			<p className='text-center text-sm text-gray-600'>
				{created ? null : 'Already have an account? '}
				<Link to='/login' className='text-pi-red hover:underline'>
					{created ? 'Back to sign in' : 'Sign in'}
				</Link>
			</p>
		</AuthCard>
	);
}
